import React, { useCallback, useState } from 'react';

import { useGraphQLMutation } from '@betterment/graphql';
import { Button, Dialog, H1, HStack, Text } from '@betterment/samba-react';

import { UnenrollClientFromSecuritiesLendingDocument } from './graphql/mutation.unenrollClientFromSecuritiesLending.generated';

interface SecuritiesLendingUnenrollDialogProps {
  memberId: string | null;
  memberName: string | undefined;
  onClose: () => void;
  onSuccess: () => void;
}

export const SecuritiesLendingUnenrollDialog: React.FC<SecuritiesLendingUnenrollDialogProps> = ({
  memberId,
  memberName,
  onClose,
  onSuccess,
}) => {
  const [unenrollClientFromSecuritiesLending, { loading }] = useGraphQLMutation(
    UnenrollClientFromSecuritiesLendingDocument
  );

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleUnenroll = useCallback(async () => {
    if (!memberId) return;
    setErrorMessage(null);
    const result = await unenrollClientFromSecuritiesLending({ variables: { input: { clientId: memberId } } });
    if (result.data?.unenrollClientFromSecuritiesLending.displayableErrors.length === 0) {
      onSuccess();
    } else {
      setErrorMessage('Something went wrong. Try again.');
    }
  }, [memberId, onSuccess, unenrollClientFromSecuritiesLending, setErrorMessage]);

  return (
    <Dialog
      open={memberId !== null}
      overlay="default"
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      <Dialog.Content size="large" css={{ display: 'flex', flexDirection: 'column', gap: 'space.150', p: 'space.400' }}>
        <Dialog.Title asChild={true}>
          <H1 variant="title300">
            Are you sure you want to turn off securities lending for {memberName ?? 'this client'}?
          </H1>
        </Dialog.Title>
        <Dialog.Description asChild={true}>
          <Text variant="text100" css={{ mb: 'space.50' }}>
            This change will be effective immediately. Your client will receive an email stating that they have been
            unenrolled.
          </Text>
        </Dialog.Description>
        <HStack css={{ mt: 'space.200', justifyContent: 'flex-start' }}>
          <Button
            variant="solidPrimary"
            onClick={() => {
              void handleUnenroll();
            }}
            loading={loading}
          >
            Yes, turn off
          </Button>
          <Button variant="solidSecondary" onClick={onClose} css={{ ml: 'space.200' }}>
            Cancel
          </Button>
        </HStack>
        {errorMessage && <Text color="text.critical">{errorMessage}</Text>}
      </Dialog.Content>
    </Dialog>
  );
};
